import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useEffect, useMemo, useState } from "react"
import type {
  CandidateArticle,
  IssuerScopeProposal,
  ReaderHint,
  UncoveredClaim,
} from "../../../../../api/types"
import type { WorklistIssue } from "../../studio/cellState"
import type { FilterToken } from "../../studio/TableSearch"
import { BusyLabel } from "../footer/BusyLabel"
import { openIssuesByCard } from "../footer/nextOpenPoint"
import {
  useConformFramework,
  useConformProgress,
} from "../footer/useConformFramework"
import {
  conformBlockReason,
  type ConformBlockReason,
  type FrameworkDerivation,
} from "../mapping/deriveFrameworkVerdict"
import type { FrameworkEditionInfo } from "../mapping/editionInfo"
import { hasConditionsRow } from "../mapping/verdictDisplay"
import {
  ArticleBrowserSheet,
  type ArticleSheet,
} from "../scope/ArticleBrowserSheet"
// The prototype's own stylesheet, ported verbatim (see its module doc).
// Imported here (this pane's top-level component) rather than duplicated —
// CSS imports are idempotent across modules, so the shell importing it too
// (`StudioV2Page`) is a no-op, not a conflict.
import "../studioV2.css"
import type { StatementCard } from "../useStudioV2State"
import {
  AddStatementControl,
  type ExternalEvidencePick,
} from "./AddStatementControl"
import { R1Coachmark } from "./R1Coachmark"
import { RemainderCard } from "./RemainderCard"
import {
  SubstanceEditorSheet,
  type SubstanceSheet,
} from "./SubstanceEditorSheet"
import { UnitCard } from "./UnitCard"

/** Everything that can take the place of the statement cards under the pane
 *  header while the PDF stays live beside it: the article browser (scope /
 *  remainder, round 3) or the substance table (round 6). One slot, owned by
 *  `StudioV2Layout`, the two can never stand at once. */
export type MaskSheet = ArticleSheet | SubstanceSheet

export interface ReviewMaskPaneProps {
  declId: string
  /** The active Verordnung's own slug — what the bundling gesture and its
   *  mutations address (distinct from `frameworkName`, its display label). */
  framework: string
  /** 0-based position of the active Verordnung among `frameworkCount`. */
  frameworkIndex: number
  frameworkCount: number
  frameworkName: string
  derivation: FrameworkDerivation | null
  /** The active framework's statements, in document order. */
  cards: StatementCard[]
  /** The active framework's own open worklist issues — what "Verordnung als
   *  geprüft markieren" bundles over. Empty once the framework is geprüft. */
  openIssues: WorklistIssue[]
  /** The declaration's whole resolved article pool — threaded down to every
   *  unit card's Geltung control and the article browser (`scopeWriteFor`'s
   *  "Alle Artikel" target, the browser's row source). */
  pool: CandidateArticle[]
  /** MC_ISSUER_SCOPE_PROPOSAL (TUR-1720): the declaration's ONE issuer
   *  proposal, threaded to every unit card and the article browser alike,
   *  so every statement offers the same one. Null/omitted with the flag off. */
  issuerProposal?: IssuerScopeProposal | null
  /** STEP C3-b3, paged review: the pool (`/scope`) has not landed yet. The article
   *  browser shows that it is loading rather than an empty pool, and the substance
   *  sheet does not call the pool empty. */
  poolLoading?: boolean
  /** Paged review: the pool (`/scope`) read failed. The article browser shows an
   *  error with a retry instead of an empty pool it could not recover from. */
  poolError?: boolean
  onRetryPool?: () => void
  /** `useStudioV2State`'s `uncoveredClaimsByArticle` — threaded to every
   *  unit card's article row and "alle Artikel" note. Optional, defaults
   *  empty so an older render (and every test predating it) keeps working. */
  uncoveredClaimsByArticle?: Map<string, UncoveredClaim[]>
  /** The active Verordnung's inquiry template reader hints, keyed by article
   *  number (`useStudioV2State`'s `readerHintsByFramework`): threaded to every
   *  unit card (current and legacy) and to the remainder card. Optional,
   *  defaults empty. */
  readerHintsByArticle?: Map<string, ReaderHint[]>
  /** Lands a reader hint's sheet row (1-based) in the workbook pane
   *  (`StudioV2Layout`'s own `handleJumpToSheetRow`): threaded with
   *  `readerHintsByArticle` to every card that notes hints. Omitted (a PDF):
   *  the hint lines stay plain notes. */
  onJumpToSheetRow?: (sheet: string, row: number) => void
  /** Whether the declaration's document is a workbook (`StudioV2Layout`'s own
   *  `isWorkbook`, file-name derived) — gates every unit card's page-jump
   *  button off (see `UnitCard`'s own doc for why). */
  isWorkbook: boolean
  /** "S. {page}" / the card header → the document pane's evidence flash.
   *  Threaded straight through to every unit card unchanged. */
  onJumpToEvidence: (
    card: StatementCard,
    index: number,
    evidenceIndex?: number,
  ) => void
  /** The active Verordnung's Referenzlisten-Achse (`state.editionInfo`) —
   *  threaded to every unit card's REFERENZLISTE row. Null: no row. */
  editionInfo?: FrameworkEditionInfo | null
  /** The URL-addressed card requested by an approve-gate navigation. */
  focusedStatementId?: string | null
  /** The step that navigation named (the URL's `&step=`,
   *  `StudioV2Layout::goToOpenPoint`'s own doc), threaded to the focused
   *  card's own `focusStep` (`UnitCard`'s own doc: step 2/3 also open that
   *  step's dialog). Null/omitted, every existing caller: expand only. */
  focusedStep?: 1 | 2 | 3 | null
  /** TUR-1445, "+ Aussage hinzufügen" — the declaration's frameworks and their
   *  display names for the dialog's Verordnung pick, the landing callback for
   *  the created card, and the evidence-picker hand-off. All optional: a
   *  standalone render (and every existing test) gets the active framework as
   *  the only choice and a dialog without the picker button. */
  frameworks?: string[]
  nameOf?: (framework: string) => string
  onStatementCreated?: (framework: string, statementId: string) => void
  externalPick?: ExternalEvidencePick | null
  onAddDialogOpenChange?: (open: boolean) => void
  /** Fired once the bundling gesture has ACTUALLY checked this Verordnung off
   *  (`ok` with no article-scope work left standing) — `StudioV2Layout` wires
   *  it to the SAME `advancePastActive` the Enter shortcut already uses, so
   *  the button and the key land on the next un-geprüfte Verordnung alike
   *  (product-owner feedback, 03.09.: the click left the reviewer parked on a
   *  Verordnung with nothing left to do on it). Optional: a standalone render
   *  (and every test predating it) simply does not advance. */
  onFrameworkChecked?: (framework: string) => void
  /** TUR-1449 PR 3 — bumped by `StudioV2Layout.goToOpenPoint` on EVERY jump
   *  (Weiter and the open-points popover alike), so re-targeting the SAME
   *  statement still re-runs the focus effect below. Without it, a URL patch
   *  to the already-focused card changes nothing and the jump silently
   *  no-ops. */
  focusNonce?: number
  /** Articles the active Verordnung's open Rückfrage names (0 when none). A
   *  sent Rückfrage with articles no longer blocks the conform gesture: approve
   *  holds those articles back (see `conformBlockReason`). */
  inquiryArticleCount?: number
  /** Questions collected for the active Verordnung but not sent yet (0 when
   *  none). Never blocks the conform gesture on its own (TUR-1581), the
   *  footer only notes them. */
  draftedQuestionCount?: number
  /** The sheet standing in for the cards (article browser, round 3; the
   *  substance table, round 6). Owned by `StudioV2Layout`, not this pane:
   *  Esc must close the sheet before it leaves the workspace, a framework
   *  switch must close it, and the document pane opens its find bar along
   *  with the browser kinds. Null: the cards. A sheet naming a card that is
   *  no longer in `cards` (a refetch dropped it) falls back to the cards
   *  too, never to an empty pane. */
  maskSheet: MaskSheet | null
  onMaskSheetChange: (sheet: MaskSheet | null) => void
  /** The document pane's find bar (chips + live input): the browser filters
   *  its table by the same expression while it is open. */
  findTokens: FilterToken[]
  findSearch: string
  /** Clears the shared expression above through the SAME setters the
   *  document pane's own bar uses, threaded straight to
   *  `ArticleBrowserSheet` (and from there to `ArticleBrowser`'s filter
   *  chip) unchanged: this pane owns no find state of its own to reconcile.
   *  Optional so an older caller/test predating it keeps rendering, just
   *  without the chip's clear control. */
  onClearFind?: () => void
  /** Passed on to the article browser's search icon. */
  onFindRequest?: () => void
}

/**
 * The middle pane's CONTENTS — `StudioV2Layout` supplies the `sv2-pane
 * sv2-pane-rv` wrapper itself, this returns the three bands that go inside
 * it as a fragment. Markup mirrors the prototype's `pane-rv`: a `sv2-pnh`
 * header (position + name; the derived head verdict and its derivation
 * sentence were removed, TUR-1211 2026-09-13), then the
 * `sv2-vsec sv2-vsec-cond` band — one EDITABLE `sv2-exrow sv2-unit` card per
 * statement of the active Verordnung — and a `sv2-pfoot` carrying the
 * per-Verordnung "als geprüft" bundling gesture (B10), the prototype's own
 * `checkfw` footer button. "+ Aussage hinzufügen" renders as the prototype's
 * own dashed `.sv2-addexc` button, LIVE since TUR-1445: it opens the
 * add-statement dialog (`AddStatementControl`), whose Fundstelle comes from
 * selecting text in the document pane (the floating chip). Below the cards,
 * `AussagePanel` mirrors the prototype's `ausPanel` (aggregated across every
 * card's own `scopeVerbatim`, since this read model carries no single
 * per-block quote) — its toggle sits here, in the body, rather than in
 * `sv2-pfoot` alongside `ausBtn`/`belegBtn` in the prototype, because that
 * footer belongs to `StudioV2FooterBar`'s sibling agent, out of this pass's
 * scope.
 *
 * While a sheet is open (`maskSheet`) it takes the place of the cards AND
 * the footer under the header: the article browser (`ArticleBrowserSheet`)
 * or the substance table (`SubstanceEditorSheet`), so the reviewer keeps
 * the Verordnung and the document in view while picking articles or typing
 * values off the PDF.
 */
export function ReviewMaskPane({
  declId,
  framework,
  frameworkIndex,
  frameworkCount,
  frameworkName,
  derivation,
  cards,
  openIssues,
  pool,
  poolLoading = false,
  poolError = false,
  onRetryPool,
  uncoveredClaimsByArticle,
  readerHintsByArticle,
  onJumpToSheetRow,
  isWorkbook,
  onJumpToEvidence,
  editionInfo = null,
  focusedStatementId = null,
  focusedStep = null,
  frameworks,
  nameOf,
  onStatementCreated,
  externalPick = null,
  onAddDialogOpenChange,
  onFrameworkChecked,
  focusNonce = 0,
  inquiryArticleCount = 0,
  draftedQuestionCount = 0,
  maskSheet,
  onMaskSheetChange,
  findTokens,
  findSearch,
  onClearFind,
  onFindRequest,
  issuerProposal = null,
}: ReviewMaskPaneProps) {
  const { translate } = useTranslation()
  const { conformFramework, isRunning } = useConformFramework(declId)
  const [busy, setBusy] = useState(false)
  // TUR-1680: a run from ANY surface (this button, Enter, focus mode's
  // button, the bulk gesture) shows here, read from the shared progress, and
  // `busy` covers this button's own click before that read has seen it.
  const conformProgress = useConformProgress(declId)
  const conforming = busy || conformProgress === "confirming"
  const conformBusy = conforming || conformProgress === "refreshing"

  useEffect(() => {
    if (focusedStatementId) focusUnit(focusedStatementId)
    // `focusNonce` re-runs this for a jump to the ALREADY-focused card (see
    // its prop doc) — effects run post-render, so a cross-framework jump
    // lands after the target card has mounted.
  }, [focusedStatementId, framework, focusNonce])

  // R1 — an unresolved scope OR a Rückfrage already sent (`deriveFrameworkVerdict`'s
  // own cascade) outranks the bundling gesture: the article scope or the
  // supplier's answer for THIS Verordnung is still open, so signing off every
  // statement underneath it would sign off work the reviewer cannot actually
  // have verified yet. Only the gesture is blocked — navigation, the other
  // Verordnungen, and Überspringen/Anfordern (both live in the document-level
  // footer, untouched here) all stay reachable, so this is never a dead end.
  //
  // GATED ON `openIssues.length > 0` TOO: an already-geprüft framework (no open
  // issues left) can still carry an R1 derivation — e.g. a NEW Rückfrage raised
  // after this Verordnung was signed off. There is no live gesture to block on
  // that framework any more, so "cannot be marked as checked yet" would be a
  // stale, misleading reason for a button that is disabled for an entirely
  // different (and already true) one — "already checked" wins instead.
  //
  // A sent Rückfrage that NAMES articles no longer blocks: approve holds exactly
  // those articles back until the supplier's reply declaration writes them, so
  // the rest of this Verordnung can be checked now. Nor does a DRAFTED one
  // (TUR-1581): nothing was sent, so there is no reply to wait for; the footer
  // only notes the collected questions (`conformBlockReason`).
  const blockReason: ConformBlockReason | null =
    openIssues.length > 0
      ? conformBlockReason(derivation, inquiryArticleCount)
      : null
  const blockedByInquiry = blockReason !== null
  const blockedTitleKey =
    blockReason === "scope-unresolved"
      ? "MC_DECL_V2_CONFORM_BLOCKED_SCOPE_TITLE"
      : "MC_DECL_V2_CONFORM_BLOCKED_INQUIRY_TITLE"
  const blockedKey =
    blockReason === "scope-unresolved"
      ? "MC_DECL_V2_CONFORM_BLOCKED_SCOPE"
      : "MC_DECL_V2_CONFORM_BLOCKED_INQUIRY"

  // E2: the framework's own quiet progress line ("N von M Aussagen
  // bestätigt"): `done` uses the SAME step-1 signature `frameworkGeprueft`
  // gates on (`openByFramework`'s own doc), never a second, drifting notion
  // of "confirmed": a card counts as done exactly when NO open step-1
  // statement issue names it. `excluded` cards never enter `total`/`done` at
  // all (module doc: an excluded statement "counts for nothing any more").
  const activeCards = cards.filter((c) => !c.excluded)
  const openStep1Ids = new Set(
    openIssues
      .filter((i) => i.unit === "statement" && i.step === 1)
      .map((i) => i.statementId ?? i.unitId),
  )
  const confirmedCount = activeCards.filter(
    (c) => !openStep1Ids.has(c.id),
  ).length

  // Every open worklist issue a statement still carries, by card, in DISPLAY
  // order (W5-4): the collapsed card's header wears them as a reason chip
  // (`UnitCard`'s own `openIssues`), so a reviewer scanning the sentence
  // list sees WHERE and WHY work is left without opening each card. The SAME
  // grouping the open-points popover reads (`openIssuesByCard`'s own doc):
  // the two can never disagree about a card's own open issues.
  const openIssuesByCardMap = openIssuesByCard(
    openIssues,
    cards.map((c) => c.id),
  )

  // E2: auto-advance after "Verordnung als geprüft markieren". This gesture
  // (`handleConform` below) is the ONE per-statement step-1 SIGNATURE
  // (`useConfirmStatement`, "Confirm statement") reachable in Studio V2 -
  // `UnitCard`'s own `handleRestore` also fires a `useReviewStatement` mutation
  // named "accept", but that flips `statement_status` off `rejected`, a
  // different concept from the step-1 confirm signature (see its own doc); it
  // is deliberately NOT this gesture.
  //
  // WIRED HERE, NOT INSIDE `useConformFramework`. That hook is shared with
  // `BulkConformGesture`'s multi-framework run: a scroll fired per statement
  // INSIDE its loop would jump a pane that may not even be the active
  // framework's, with no render between iterations to make each jump mean
  // anything. `handleConform` is this pane's own single-framework call site,
  // already holds the success branch, and already has `focusUnit` in scope.
  //
  // READS `openIssues`/`activeCards` from THIS RENDER'S PROPS, not a refetch:
  // the invalidation `conformFramework` fires is still in flight when it
  // resolves (`StudioV2Layout::advancePastActive`'s own comment documents the
  // same staleness window): so after a FULLY successful run (every step-1 -
  // and step-3: issue this gesture could touch is cleared) there is usually
  // nothing left to jump to, and this is a silent no-op, exactly the spec'd
  // "last statement just confirmed → no jump" case. It only actually finds a
  // target in the PARTIAL case: a statement whose article scope (step 2, a
  // step this gesture never touches: module doc) is still open.
  const advanceToNextOpenStatement = () => {
    // NEVER STEAL FOCUS FROM AN INPUT: `focusUnit` below calls `el.focus()`,
    // so this guard is load-bearing, not decorative.
    const active = document.activeElement
    if (
      active instanceof HTMLElement &&
      (active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable)
    ) {
      return
    }
    // STEP 2 ONLY, deliberately: NOT every `i.unit === "statement"` issue in
    // the (stale) prop. `ok: true` means `conformFramework` already walked
    // EVERY card and cleared its step-1/step-3 issues one by one (or it would
    // have returned `ok: false`: see its own module doc); their continued
    // presence in this un-refetched `openIssues` snapshot is exactly the
    // staleness the comment above names, not real open work. Step 2 (article
    // scope) is the one step the gesture never even attempts, so it is the
    // only step whose presence here is still trustworthy: the SAME filter
    // `useConformFramework`'s own `openScopeStatements` count uses.
    const openStatementIds = new Set(
      openIssues
        .filter((i) => i.unit === "statement" && i.step === 2)
        .map((i) => i.statementId ?? i.unitId),
    )
    const next = activeCards.find((c) => openStatementIds.has(c.id))
    if (next) focusUnit(next.id)
  }

  const handleConform = async () => {
    if (blockedByInquiry) {
      // Defense in depth: the button below is also `disabled` while this holds,
      // so a native click never reaches here — this guard only matters if a
      // future caller invokes the handler directly.
      toast.error(translate(blockedKey, { framework: frameworkName }))
      return
    }
    setBusy(true)
    // A single-framework call: no bulk run to thread `rows` across, so the
    // returned row set is ignored — `conformFramework` falls back to the
    // live query snapshot on its own (see its own doc).
    const { ok, openScopeStatements } = await conformFramework(
      framework,
      cards,
      openIssues,
    )
    setBusy(false)
    if (!ok) return
    // Success is worded as WHAT WAS DONE ("saved"), not what was thereby
    // achieved ("geprüft") — the checkmark itself only flips once the
    // invalidation this mutation fired has actually refetched the worklist,
    // which is still in flight when `conformFramework` resolves. And when the
    // run left article-scope work standing (the one step the gesture cannot
    // clear), plain success would contradict the still-unchecked navigator
    // one pane over — the partial message names what remains and where.
    if (openScopeStatements > 0) {
      toast.warning(
        translate("MC_DECL_V2_CONFORM_PARTIAL_SCOPE", {
          n: openScopeStatements,
        }),
      )
      // The Verordnung is NOT geprüft — its article scope still awaits the
      // card's own Apply, the one step this gesture cannot fire. Moving on to
      // the next Verordnung would walk past work that is right here, so the
      // partial case keeps the in-pane jump instead.
      advanceToNextOpenStatement()
      return
    }
    toast.success(translate("MC_DECL_V2_CONFORM_SUCCESS"))
    // Nothing left on this Verordnung: hand over to the shell, which advances
    // to the next un-geprüfte one exactly as the Enter shortcut does. Without
    // a handler wired, `advanceToNextOpenStatement` stays the fallback — a
    // no-op after a fully successful run (see its own doc), never a jump to a
    // card with no open work.
    if (onFrameworkChecked) onFrameworkChecked(framework)
    else advanceToNextOpenStatement()
  }

  // NO GLOBAL FRAMEWORK VERDICT IN THE MASK (TUR-1211, Tobi 10.09, product decision
  // 2026-09-11). The head pill (`HeadVerdictControl`), its "N weitere Aussagen"
  // note, the "Ableitung: …" strip (`DeriveStrip`) and the "effektiv konform" badge
  // all restated the R1–R4 rollup ABOVE the statements as a second truth the
  // reviewer had to reconcile with the cards, and worded it as the supplier's own
  // claim ("per the declaration") when it was a derivation. The reviewer works
  // statement by statement; the rollup lands where it has effect — the article and
  // supplier views after approve. The derivation itself is untouched: Weiter, the
  // Rückfrage coachmark below, the navigator dot and the bulk conform gesture still
  // read it. Follow-up decision (2026-09-13, TUR-1211 wave 2): a
  // framework-level override lives nowhere — `HeadVerdictControl`,
  // `DeriveStrip` and the manual-override machinery (`useManualOverride`,
  // pause/resume) are deleted, not just unmounted. Per-statement verdict
  // editing (`useEditStatementVerdict` in `UnitCard`/`EditionRow`) stays.
  //
  // NO "AUSSAGE LAUT DOKUMENT" PANEL EITHER (Erik 11.09, product decision
  // 2026-09-13): `AussagePanel` repeated each card's `scopeVerbatim` quote a
  // second time below the cards, and reviewers could not tell those "statements
  // per the document" apart from the statements above. The quote already
  // lives on the card (its evidence rows and the scope wording), so the panel
  // is unmounted the same way; the component stays in the tree.

  // The sheet only stands while it still names something to show: a scope or
  // substances sheet whose card a refetch dropped falls back to the cards.
  // One open/closed flag per card id, kept HERE because this pane outlives
  // both things that unmount a card: the regulation switch swaps `cards`,
  // and an open mask sheet replaces the whole list (the ternary below).
  // A card-local flag came back collapsed after either (live test, wave 5).
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const setCardExpanded = (id: string, next: boolean) =>
    setExpandedIds((prev) => {
      if (prev.has(id) === next) return prev
      const out = new Set(prev)
      if (next) out.add(id)
      else out.delete(id)
      return out
    })

  const sheetCard =
    maskSheet && maskSheet.kind !== "remainder"
      ? cards.find((c) => c.id === maskSheet.cardId)
      : undefined
  const substanceSheet =
    maskSheet?.kind === "substances" && sheetCard
      ? {
          focusKey: maskSheet.focusKey,
          card: sheetCard,
          // W5 step3-chip: this card's own step-3 issue, for the sheet's
          // "Bestätigen" button, the SAME per-card grouping the collapsed
          // cards' own chips read (`openIssuesByCardMap`), so the button and
          // the chip can never disagree about whether this card still has
          // open step-3 work.
          step3Issue: (openIssuesByCardMap.get(sheetCard.id) ?? []).find(
            (i) => i.step === 3,
          ),
        }
      : null
  const articleSheet =
    maskSheet &&
    maskSheet.kind !== "substances" &&
    (maskSheet.kind === "remainder" || sheetCard !== undefined)
      ? maskSheet
      : null
  // Review finding (wave 5, task B): a verdict edit can move the sheet's OWN
  // card away from a conditioned verdict (compliant_with_conditions /
  // non_compliant) while the substances sheet stands open on it, leaving it
  // over a card with no STOFFE row left to edit at all. `hasConditionsRow`
  // is the SAME predicate `UnitCard` gates its own row on (no second copy
  // of the verdict list to drift out of sync). Routed through
  // `onMaskSheetChange(null)`, the ONE close path every other case already
  // uses (Fertig/Cancel/Apply/Escape), so `StudioV2Layout::
  // handleMaskSheetChange` also clears the URL's `step` here, not a bare
  // render-time fallback.
  //
  // `onMaskSheetChange` is deliberately NOT a dependency (review):
  // `StudioV2Layout::handleMaskSheetChange` is a plain closure, a fresh
  // reference every render, not `useCallback`-wrapped, so tracking it would
  // re-run this effect on every unrelated parent render, not just the ones
  // where `maskSheet`/`sheetCard` themselves changed. Correctness never
  // depends on which render's closure fires: the guard is a pure read of
  // `maskSheet`/`sheetCard`, and the SAME `handleMaskSheetChange(null)` call
  // is what every other close path in `StudioV2Layout` already reaches.
  // Self-limiting besides: `handleMaskSheetChange(null)` sets `maskSheet`
  // itself to null in the SAME batch, so the guard is false on every render
  // after the first one that calls it, so this cannot loop.
  useEffect(() => {
    if (
      maskSheet?.kind === "substances" &&
      sheetCard &&
      !hasConditionsRow(sheetCard.verdict)
    ) {
      onMaskSheetChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskSheet, sheetCard])
  // Number -> name for the substance sheet's per-article rows, built once
  // per `pool` identity so the editor's `slots` memo never sees a fresh Map
  // object every render (H3, previously `UnitCard`'s own memo).
  const articleNames = useMemo(
    () => new Map(pool.map((a) => [a.articleNumber, a.name])),
    [pool],
  )

  return (
    <>
      <div className="sv2-pnh">
        {/* Prototype's `single` check (`renderReview`'s `pnh`): the position
            index only earns its place next to a navigator that shows the
            OTHER Verordnungen — a single-Verordnung declaration has none to
            count against, so the index is omitted entirely, not just hidden. */}
        {frameworkCount > 1 && (
          <span className="sv2-n">
            {frameworkIndex + 1}/{frameworkCount}
          </span>
        )}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="sv2-t sv2-cur">{frameworkName}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{frameworkName}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {substanceSheet ? (
        <SubstanceEditorSheet
          declId={declId}
          card={substanceSheet.card}
          index={cards.indexOf(substanceSheet.card)}
          poolEmpty={pool.length === 0 && !poolLoading && !poolError}
          articleNames={articleNames}
          focusKey={substanceSheet.focusKey}
          onClose={() => onMaskSheetChange(null)}
          step3Issue={substanceSheet.step3Issue}
        />
      ) : articleSheet && poolError ? (
        <div
          className="flex items-center gap-2 px-4 py-6 text-[12.5px] text-grey5"
          role="alert"
          data-testid="article-browser-pool-error"
        >
          <span>{translate("MC_DECL_V2_POOL_LOAD_ERROR")}</span>
          {onRetryPool && (
            <button
              type="button"
              onClick={onRetryPool}
              className="sv2-btn sv2-btn-sm"
            >
              {translate("MC_DECL_BTN_RETRY")}
            </button>
          )}
        </div>
      ) : articleSheet && poolLoading ? (
        <div
          className="px-4 py-6 text-[12.5px] text-grey5"
          role="status"
          aria-busy="true"
          data-testid="article-browser-pool-loading"
        >
          {translate("MC_DECL_V2_POOL_LOADING")}
        </div>
      ) : articleSheet ? (
        <ArticleBrowserSheet
          // F13 (review, wave 5): forces a remount when the sheet moves to a
          // different card (or to the remainder view). `ArticleBrowser`'s own
          // `staged` set seeds ONLY on mount (`useState(() => new
          // Set(applied))`), so without this an unapplied tick made on one
          // card's sheet survived into the next card's, ticking an article
          // that card never had.
          key={
            articleSheet.kind === "scope" ? articleSheet.cardId : "remainder"
          }
          declId={declId}
          framework={framework}
          sheet={articleSheet}
          cards={cards}
          pool={pool}
          issuerProposal={issuerProposal}
          findTokens={findTokens}
          findSearch={findSearch}
          onClearFind={onClearFind}
          onFindRequest={onFindRequest}
          onClose={() => onMaskSheetChange(null)}
          onStatementCreated={onStatementCreated}
        />
      ) : (
        <>
          <R1Coachmark derivation={derivation} />
          <div className="sv2-pbody sv2-rvbody">
            <div className="sv2-vsec sv2-vsec-cond">
              {/* E2: the framework's quiet progress counter. The row's former
                  "{Verordnung} · korrigierbar" strap was pure meta-labeling with
                  no reviewer value (product-owner feedback, 01.09.), so the row
                  itself now only exists while there is progress to count: a
                  single-statement mask renders no row at all. */}
              {activeCards.length > 1 && (
                <div className="sv2-rsl">
                  {/* `.sv2-rsl` itself sets mono/uppercase/tracked (the strap's
                      own look): this counter resets all three back to the quiet
                      sans label family, or it renders "N OF M STATEMENTS
                      CONFIRMED" in mono caps. `ml-auto` keeps it right-aligned,
                      where the strap's own tail used to push it. */}
                  <span className="ml-auto flex-none font-sans text-[11.5px] tracking-normal text-grey5 normal-case">
                    {translate("MC_DECL_V2_MASK_CONFIRMED_COUNT", {
                      done: confirmedCount,
                      total: activeCards.length,
                    })}
                  </span>
                </div>
              )}
              <div className="space-y-2">
                {cards.map((card, i) => {
                  const cardOpenIssues = openIssuesByCardMap.get(card.id) ?? []
                  return (
                    <div
                      key={card.id}
                      id={unitAnchorId(card.id)}
                      tabIndex={-1}
                      className="sv2-unit-anchor"
                      // W5-4: a card's own open-issue facts, for tests (and any
                      // future e2e probe) that need them without reaching into
                      // the chip's translated text.
                      data-statement-id={card.id}
                      data-open-count={cardOpenIssues.length}
                      data-open-steps={cardOpenIssues
                        .map((i) => i.step)
                        .join(",")}
                    >
                      <UnitCard
                        declId={declId}
                        index={i}
                        card={card}
                        pool={pool}
                        uncoveredClaimsByArticle={uncoveredClaimsByArticle}
                        readerHintsByArticle={readerHintsByArticle}
                        onJumpToSheetRow={onJumpToSheetRow}
                        isWorkbook={isWorkbook}
                        onJumpToEvidence={onJumpToEvidence}
                        editionInfo={editionInfo}
                        frameworkName={frameworkName}
                        focused={focusedStatementId === card.id}
                        expanded={expandedIds.has(card.id)}
                        onExpandedChange={(next) =>
                          setCardExpanded(card.id, next)
                        }
                        focusNonce={focusNonce}
                        focusStep={
                          focusedStatementId === card.id ? focusedStep : null
                        }
                        openIssues={cardOpenIssues}
                        // How many statements this Verordnung puts on
                        // screen, for the step-1 chip's redundancy rule
                        // (`UnitCard`'s own `statementCount`): with one
                        // card, "Verordnung als geprüft markieren" below
                        // already names that statement unambiguously.
                        // The RENDERED count, excluded cards included —
                        // they are on screen and are what makes the
                        // button's wording ambiguous, whatever they
                        // contribute to `confirmedCount`.
                        statementCount={cards.length}
                        // Is the gesture that chip defers to actually
                        // pressable? The SAME `blockedByInquiry` the
                        // footer button below reads, so the card cannot
                        // hide a chip whose replacement this very render
                        // is disabling. Deliberately not the button's
                        // `disabled` expression in full: `busy`/`isRunning`
                        // are in-flight states of a mutation the reviewer
                        // just started, and a chip that blinks back during
                        // a save would read as work reappearing.
                        //
                        // The button's third disabled case, "already
                        // checked" (`openIssues.length === 0`), is
                        // left out because it cannot strand a
                        // suppressed chip: `openIssuesByCardMap` is
                        // grouped out of THIS SAME `openIssues` list,
                        // so a card carries a chip only while the list
                        // is non-empty, which is exactly when that
                        // case does not fire. Pinned by this file's
                        // own "shows no chip at all once the framework
                        // has nothing open" test.
                        conformAvailable={!blockedByInquiry}
                        issuerProposal={issuerProposal}
                        onOpenArticleBrowser={(view, geltung) =>
                          onMaskSheetChange({
                            kind: "scope",
                            cardId: card.id,
                            view,
                            geltung,
                          })
                        }
                        onOpenSubstances={(focusKey) =>
                          onMaskSheetChange({
                            kind: "substances",
                            cardId: card.id,
                            focusKey,
                          })
                        }
                      />
                    </div>
                  )
                })}
              </div>
              {/* The remainder card after the last statement (review-mask
                  prototype port): the articles no statement covers yet, one
                  click away from becoming the last statement. It also hosts the
                  quote-based path: the add-statement dialog (`AddStatementControl`,
                  LIVE since TUR-1445) opens from its "Aussage aus Textstelle"
                  button; the trigger renders outside the Dialog and opens it by
                  callback (`renderTrigger`, same reasoning as
                  RemoveStatementControl). */}
              <AddStatementControl
                declId={declId}
                frameworks={frameworks ?? [framework]}
                activeFramework={framework}
                nameOf={nameOf ?? ((fw) => fw)}
                pool={pool}
                isWorkbook={isWorkbook}
                onCreated={onStatementCreated ?? (() => {})}
                externalPick={externalPick}
                onOpenStateChange={onAddDialogOpenChange}
                renderTrigger={(openDialog) => (
                  <RemainderCard
                    declId={declId}
                    framework={framework}
                    cards={cards}
                    pool={pool}
                    onStatementCreated={onStatementCreated}
                    onAddFromQuote={openDialog}
                    readerHintsByArticle={readerHintsByArticle}
                    onJumpToSheetRow={onJumpToSheetRow}
                    onOpenArticleBrowser={() =>
                      onMaskSheetChange({
                        kind: "remainder",
                        view: { kind: "all" },
                      })
                    }
                  />
                )}
              />
              {/* A workbook has no selectable text, so no chip to point at. */}
              {!isWorkbook && (
                <p className="sv2-hint mt-2 px-1">
                  {translate("MC_DECL_V2_REST_TIP")}
                </p>
              )}
            </div>
          </div>
          <div className="sv2-pfoot">
            {(() => {
              const disabledReason = blockedByInquiry
                ? translate(blockedTitleKey)
                : openIssues.length === 0
                  ? translate("MC_DECL_V2_CONFORM_ALREADY_DONE")
                  : undefined
              const conformButton = (
                <button
                  type="button"
                  disabled={
                    openIssues.length === 0 ||
                    conformBusy ||
                    isRunning ||
                    blockedByInquiry
                  }
                  aria-disabled={blockedByInquiry || undefined}
                  onClick={handleConform}
                  // PRIMARY exactly while it is THE next step (product owner,
                  // 30.08.: this is the most-clicked CTA of the whole flow and
                  // read as disabled chrome). The earlier "only one yellow
                  // primary" rule still holds in effect: while a Verordnung has
                  // open statements the footer's Freigeben is gated anyway, and
                  // once this framework is done the button disables and the
                  // footer takes over: exactly one lit next step at a time.
                  // A run in flight keeps it (TUR-1680): `.sv2-primary`'s
                  // heavier weight sizes the unpainted label, so dropping it
                  // mid-run narrowed the button; `[disabled]` dims it instead.
                  className={cn(
                    "sv2-act",
                    openIssues.length > 0 && !blockedByInquiry && "sv2-primary",
                  )}
                >
                  <BusyLabel busy={conformBusy}>
                    {translate("MC_DECL_V2_CONFORM_FRAMEWORK_BUTTON")}
                  </BusyLabel>
                </button>
              )
              if (!disabledReason) return conformButton
              // The wrapping span (not disabled) carries the tooltip: `.sv2-act
              // [disabled]` gets `pointer-events:none`, so hover/focus events on
              // the button itself would never reach a tooltip trigger there.
              // `inline-flex`, not a bare span: `.sv2-pfoot` is itself a flex
              // row (`gap`, `align-items:center`); a bare span blockifies as a
              // flex item and can add line-box leading the button never had.
              return (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">{conformButton}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{disabledReason}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )
            })()}
            {/* TUR-1680: what the run is doing, beside its button. One line,
                cut short rather than wrapped (`.sv2-conform-progress`), so the
                footer never grows mid-run. */}
            {conformBusy && (
              <span className="sv2-gate sv2-conform-progress" role="status">
                {translate(
                  conforming
                    ? "MC_DECL_V2_CONFORM_PROGRESS_CONFIRMING"
                    : "MC_DECL_V2_CONFORM_PROGRESS_REFRESHING",
                )}
              </span>
            )}
            {/* The design's own footer-hint slot (`sv2-gatehint`, ported from the
                prototype alongside `StudioV2FooterBar`'s approve-gate reason),
                content-framed per the product rule: names WHY, then what stays
                reachable, so this never reads as a dead end. */}
            {blockedByInquiry ? (
              <span className="sv2-gatehint" role="status">
                {translate(blockedKey, { framework: frameworkName })}
              </span>
            ) : (
              <>
                {/* The other disabled state: nothing is left to check. The
                    tooltip says so too, but a tooltip on a disabled button is
                    hover-only, so the reason is plain text as well. */}
                {draftedQuestionCount > 0 ? (
                  // Drafts are not open issues (collected questions, not
                  // worklist items), so both can hold at once. One live
                  // region at a time: pending drafts say more than "done".
                  <span className="sv2-gatehint" role="status">
                    {translate("MC_DECL_V2_CONFORM_DRAFTED_NOTE", {
                      n: draftedQuestionCount,
                    })}
                  </span>
                ) : (
                  openIssues.length === 0 &&
                  !conformBusy && (
                    <span className="sv2-gatehint" role="status">
                      {translate("MC_DECL_V2_CONFORM_ALREADY_DONE")}
                    </span>
                  )
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}

/** The DOM id every unit card's anchor wrapper carries (`cards.map`'s own
 *  wrapper div, above): the ONE lookup key `focusUnit` scrolls/focuses a
 *  card by (the URL-focused statement, the post-conform advance), since
 *  `UnitCard` itself is read-only context here and carries no id of its own. */
function unitAnchorId(cardId: string): string {
  return `sv2-unit-${cardId}`
}

/** Scrolls the named unit card into view and moves focus onto its anchor —
 *  the wrapper is `tabIndex={-1}` (never in Tab order, only a programmatic
 *  focus target) so this also gives keyboard/screen-reader users a landing
 *  point without adding a stop to the normal tab sequence. A silent no-op
 *  when the id is not on the page (a stale target after the framework
 *  switched underneath it): never throws. */
function focusUnit(cardId: string): void {
  const el = document.getElementById(unitAnchorId(cardId))
  if (!el) return
  el.scrollIntoView({ behavior: "smooth", block: "center" })
  el.focus({ preventScroll: true })
}
